// =============================================================
// AuthContext — wraps /api/auth/me, login, logout
// =============================================================
// Session-based (cookie). Works unchanged against the PHP backend:
// login establishes a PHP session, /api/auth/me reads it, logout
// destroys it. No JWT is ever read client-side.
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { api, setUnauthorizedHandler, type User } from "../lib/api";
import { initPushNotifications, unsubscribePush } from "../lib/pushManager";

interface AuthValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAgent: boolean;
  isIt: boolean;
  isStaff: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get<{ user: User }>("/api/auth/me");
      setUser(data.user);
      // Fire-and-forget: register push after session restore.
      initPushNotifications().catch(() => {});
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, [refresh]);

  const login = async (email: string, password: string) => {
    const { data } = await api.post<{ user: User }>("/api/auth/login", {
      email,
      password,
    });
    setUser(data.user);
    // Fire-and-forget: register push after login.
    initPushNotifications().catch(() => {});
    return data.user;
  };

  const logout = async () => {
    try {
      // Unsubscribe push before logout (best-effort).
      await unsubscribePush();
      await api.post("/api/auth/logout");
    } finally {
      setUser(null);
    }
  };

  const value: AuthValue = {
    user,
    loading,
    isAuthenticated: !!user,
    isAgent: user?.role === "AGENT",
    isIt: user?.role === "IT",
    isStaff: user?.role === "IT" || user?.role === "ADMIN",
    login,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
